import { useContext, type RefObject } from 'react';

import { useIntl } from 'react-intl';

import { TeamInlinePreviewContext } from '../store/context';
import { messages as archivedMessages } from '../ui/components/ArchivedLozenge';

import { useTruncationDetection } from './useTruncationDetection';

/**
 * Hook that determines whether to display verified and archived badges.
 * Uses truncation detection to conditionally hide badges when text is truncated,
 * unless preserveBadges is enabled in the context.
 */
function useShowBadges({
	showVerifiedBadge,
	showArchivedBadge,
}: {
	showVerifiedBadge: boolean;
	showArchivedBadge: boolean;
}): {
	elementRef: RefObject<HTMLElement>;
	showVerified: boolean;
	showArchived: boolean;
} {
	const { isArchived, isVerified, preserveBadges } = useContext(TeamInlinePreviewContext);
	const { formatMessage } = useIntl();

	const hasVerified = Boolean(isVerified && showVerifiedBadge);
	const hasArchived = Boolean(isArchived && showArchivedBadge);

	// Get the translated archived text for width calculation
	const archivedText = hasArchived ? formatMessage(archivedMessages.archived) : '';

	const { elementRef, isTruncated } = useTruncationDetection({
		hasVerified,
		hasArchived,
		archivedText,
	});

	const canShowBadges = preserveBadges || !isTruncated;
	const showVerified = hasVerified && canShowBadges;
	const showArchived = hasArchived && canShowBadges;

	return { elementRef, showVerified, showArchived };
}
export { useShowBadges };
